import type { ComponentLikeType } from 'elements/hardwareSelectorV2/types'
import type LineItemType from 'pages/ordering/OrderLineItem'
import type { ProjectOrderDetail } from 'pages/ordering/type'
import type { DataProviderProxy, Identifier } from 'react-admin'
import ProjectOrderPresenter from '../ProjectOrderPresenter/projectOrderPresenter'
import type { BasicComponentLineItemType, BomLineItemType, HardwareOrderProjectRecord, SystemDesignType } from '../type'
import type { SystemSelectDropdownOptionType } from './table/SystemSelectDropdown'
import type { BomDataResponseType, DownloadBomFileType } from './table/viewBom/type'
import type { ProjectTableStore, SelectedSystemMapType } from './useProjectTable'

export type FetchComponentsDataAsync = (codes: string[]) => Promise<ComponentLikeType[]>

export class ProjectTablePresenter {
  private updateProjectTableStore: React.Dispatch<React.SetStateAction<ProjectTableStore>>
  private orderingPresenter: ProjectOrderPresenter
  private dataProvider: DataProviderProxy
  private fetchComponentsData: FetchComponentsDataAsync

  constructor(
    updateProjectTableStore: React.Dispatch<React.SetStateAction<ProjectTableStore>>,
    orderingPresenter: ProjectOrderPresenter,
    dataProvider: DataProviderProxy,
    fetchComponentsData: FetchComponentsDataAsync
  ) {
    this.updateProjectTableStore = updateProjectTableStore
    this.orderingPresenter = orderingPresenter
    this.dataProvider = dataProvider
    this.fetchComponentsData = fetchComponentsData
  }

  onSelect = async (newIds: Identifier[]) => {
    // disabled
  }

  private setSystemLoading = (systemId: number, isLoading: boolean) => {
    this.updateProjectTableStore((state) => {
      const newLoadingSystemIds = new Set(state.loadingSystemIds)
      if (isLoading) {
        newLoadingSystemIds.add(systemId)
      } else {
        newLoadingSystemIds.delete(systemId)
      }
      return { ...state, loadingSystemIds: newLoadingSystemIds }
    })
  }

  private extractCodesFromSystemDesign = (systemDesign: SystemDesignType): string[] => {
    const codes: string[] = []

    if (systemDesign.bom_line_items && systemDesign.bom_line_items.length > 0) {
      systemDesign.bom_line_items.forEach((item: BomLineItemType) => {
        if (item.code) {
          codes.push(item.code)
        }
      })
    }

    const categories: Array<'batteries' | 'inverters' | 'modules' | 'others'> = [
      'batteries',
      'inverters',
      'modules',
      'others',
    ]
    categories.forEach((category) => {
      systemDesign[category]?.forEach((item: BasicComponentLineItemType) => {
        if (item.code) {
          codes.push(item.code)
        }
      })
    })

    return [...new Set(codes)] // Deduplicate
  }

  onToggle = async ({
    projectId,
    systemId,
    record,
  }: {
    projectId: number
    systemId: number
    record: HardwareOrderProjectRecord
  }): Promise<{ didChange: boolean; added?: { itemCount: number; subtotal: number } }> => {
    const lineItems = this.orderingPresenter.getLineItemsBySystemId(systemId)

    if (lineItems.length > 0) {
      this.orderingPresenter.deleteLineItemsBySystemId(systemId)
      return { didChange: true }
    }
    const result = await this.populateLineItemsBySystemId({
      projectId,
      systemId,
      systemsDesign: record.systems,
    })
    if (result.count > 0) {
      return { didChange: true, added: { itemCount: result.itemCount, subtotal: result.subtotal } }
    }
    return { didChange: false }
  }

  populateLineItemsBySystemId = async ({
    systemsDesign,
    systemId,
    projectId,
  }: {
    systemsDesign?: SystemDesignType[]
    systemId: number
    projectId: number
  }): Promise<{ count: number; itemCount: number; subtotal: number }> => {
    const systemDesign = systemsDesign?.find((system) => system.id === systemId)
    if (!systemDesign) {
      return { count: 0, itemCount: 0, subtotal: 0 }
    }

    // Set loading state
    this.setSystemLoading(systemId, true)

    try {
      // Extract component codes from the system design
      const codes = this.extractCodesFromSystemDesign(systemDesign)

      // Fetch componentsv2 data for all codes
      const componentsData = await this.fetchComponentsData(codes)

      // Create a map of code -> ComponentLikeType for quick lookup
      const componentMap: Record<string, ComponentLikeType> = {}
      componentsData.forEach((component) => {
        componentMap[component.code] = component
      })

      // Create fully-loaded line items with ordering data
      const lineItems = this.orderingPresenter.getLineItemsFromSystemDesignDataWithComponents({
        systemDesign,
        projectId,
        systemId,
        componentMap,
      })

      this.orderingPresenter.addLineItemsAndConfirm(lineItems)

      const itemCount = lineItems.reduce((sum, li) => sum + li.quantity, 0)
      const subtotal = lineItems.reduce((sum, li) => sum + li.quantity * (li.pricePerUnit ?? 0), 0)
      return { count: lineItems.length, itemCount, subtotal }
    } catch (error) {
      console.error('Error fetching component data:', error)
      return { count: 0, itemCount: 0, subtotal: 0 }
    } finally {
      this.setSystemLoading(systemId, false)
    }
  }

  /**
   * Returns preview line items and subtotal for a system without adding to cart or setting loading state.
   * Used for the BOM preview popover on hover.
   */
  getPreviewLineItemsForSystem = async ({
    systemsDesign,
    systemId,
    projectId,
  }: {
    systemsDesign?: SystemDesignType[]
    systemId: number
    projectId: number
  }): Promise<{ lineItems: LineItemType[]; subtotal: number }> => {
    const systemDesign = systemsDesign?.find((system) => system.id === systemId)
    if (!systemDesign) {
      return { lineItems: [], subtotal: 0 }
    }

    try {
      const codes = this.extractCodesFromSystemDesign(systemDesign)
      const componentsData = await this.fetchComponentsData(codes)
      const componentMap: Record<string, ComponentLikeType> = {}
      componentsData.forEach((component) => {
        componentMap[component.code] = component
      })

      const lineItems = this.orderingPresenter.getLineItemsFromSystemDesignDataWithComponents({
        systemDesign,
        projectId,
        systemId,
        componentMap,
      })

      const subtotal = lineItems.reduce((sum, li) => sum + li.quantity * (li.pricePerUnit ?? 0), 0)
      return { lineItems, subtotal }
    } catch (error) {
      console.error('Error fetching preview component data:', error)
      return { lineItems: [], subtotal: 0 }
    }
  }

  updateCartForSystem = async ({
    projectId,
    systemId,
    record,
  }: {
    projectId: number
    systemId: number
    record: HardwareOrderProjectRecord
  }): Promise<{ itemCount: number; subtotal: number } | null> => {
    this.orderingPresenter.deleteLineItemsBySystemId(systemId)
    const result = await this.populateLineItemsBySystemId({
      projectId,
      systemId,
      systemsDesign: record.systems,
    })
    if (result.count > 0) {
      return { itemCount: result.itemCount, subtotal: result.subtotal }
    }
    return null
  }

  static getSystemDisplayName = (system: SystemDesignType): string => {
    // Multi-modules not supported in one system
    const panels: BasicComponentLineItemType = system.modules[0]

    return system.name || `${panels?.quantity || 0} Panels System`
  }

  getSystemSelectOptions = (systems?: SystemDesignType[]): SystemSelectDropdownOptionType[] => {
    return (
      systems?.map((system: SystemDesignType) => ({
        id: system.id,
        name: ProjectTablePresenter.getSystemDisplayName(system),
      })) || []
    )
  }

  populateSelectedSystemMap = (lineItems: LineItemType[]) => {
    const newSelectedSystemMap: SelectedSystemMapType = lineItems.reduce((hashMap, lineItem) => {
      lineItem.projectOrder?.forEach((detail: ProjectOrderDetail) => {
        hashMap[detail.projectId] = detail.systemId
      })
      return hashMap
    }, {})

    this.updateProjectTableStore((projectTableState: ProjectTableStore) => {
      return {
        ...projectTableState,
        selectedSystemMap: newSelectedSystemMap,
      }
    })
  }

  handleSelectSystem = async ({
    systemId,
    projectId,
    currentSystemId,
    record,
  }: {
    systemId: number
    projectId: number
    currentSystemId: number | undefined
    record: HardwareOrderProjectRecord
  }) => {
    this.updateProjectTableStore((projectTableState: ProjectTableStore) => {
      const newSelectedSystemMap = { ...projectTableState.selectedSystemMap, [projectId]: systemId }
      return {
        ...projectTableState,
        selectedSystemMap: newSelectedSystemMap,
      }
    })

    if (currentSystemId != null) {
      const lineItems = this.orderingPresenter.getLineItemsBySystemId(currentSystemId)

      if (lineItems.length > 0) {
        this.orderingPresenter.deleteLineItemsBySystemId(currentSystemId)
        await this.populateLineItemsBySystemId({ projectId, systemId, systemsDesign: record.systems })
      }
    }
  }

  getSystemSelectedId = ({
    projectId,
    soldSystemId,
    selectedSystemMap,
    systemOptions,
  }: {
    projectId: number
    soldSystemId?: number
    selectedSystemMap: SelectedSystemMapType
    systemOptions: SystemSelectDropdownOptionType[]
  }): number | undefined => {
    if (selectedSystemMap[projectId] != null) {
      return selectedSystemMap[projectId]
    }

    if (soldSystemId != null) {
      return soldSystemId
    }

    if (systemOptions.length > 0) {
      return systemOptions[0].id
    }

    return undefined
  }

  getBomData = async <T extends DownloadBomFileType>({
    projectId,
    selectedSystemId,
    fileType,
    pricingType,
  }: {
    projectId: number
    selectedSystemId: number
    fileType: T
    pricingType: string
  }): Promise<BomDataResponseType<T> | undefined> => {
    try {
      const response = await this.dataProvider.CUSTOM_GET('projects', {
        url: `projects/${projectId}/systems/${selectedSystemId}/download_bom/?file_type=${fileType}&pricing_type=${pricingType}`,
      })
      return response.data
    } catch (e) {
      console.warn(e)
      return
    }
  }
}

export default ProjectTablePresenter
